import React from 'react';
import { Shield, Clock, CheckCircle, XCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const WarrantyPage = () => {
  const { language } = useLanguage();

  const content = {
    hu: {
      title: 'Garanciális feltételek',
      intro: 'A termékekre rendeltetésszerű használat esetén az alábbi garanciaidő érvényes:',
      periods: [
        { item: 'Kerámiákra', time: '14 nap' },
        { item: 'Víztartály', time: '14 nap' },
        { item: 'Vízipipákra', time: '12 hónap', note: '(a fém alkatrészek átrozsdásodása esetén díjmentes csere)' },
      ],
      validityTitle: 'A garancia érvényessége',
      validityText: 'A garancia kizárólag a gyártási hibából eredő meghibásodásokra terjed ki, valamint olyan esetekre, amikor a termék rendeltetésszerű használata bizonyítottan nem lehetséges.',
      excludedTitle: 'A garancia nem terjed ki az alábbi esetekre:',
      excluded: [
        'Mechanikai sérülések (pl. sérült vagy elkopott menet, deformálódás, törés stb.)',
        'Nem rendeltetésszerű vagy szakszerűtlen használatból eredő károk',
        'Nem megfelelő, nem gyári vagy nem szabványos kiegészítők, fogyóeszközök használatából eredő károk',
        'A felhasználó hibájából vagy gondatlanságából keletkezett sérülések',
      ],
    },
    en: {
      title: 'Warranty Terms',
      intro: 'The following warranty period applies to products when used as intended:',
      periods: [
        { item: 'Ceramics', time: '14 days' },
        { item: 'Water tank', time: '14 days' },
        { item: 'Hookahs', time: '12 months', note: '(free replacement in case of metal parts rusting through)' },
      ],
      validityTitle: 'Warranty Validity',
      validityText: 'The warranty only covers defects resulting from manufacturing faults, as well as cases where the intended use of the product is demonstrably not possible.',
      excludedTitle: 'The warranty does not cover the following cases:',
      excluded: [
        'Mechanical damage (e.g., damaged or worn threads, deformation, breakage, etc.)',
        'Damage resulting from improper or unprofessional use',
        'Damage resulting from the use of inappropriate, non-factory or non-standard accessories and consumables',
        'Damage caused by user error or negligence',
      ],
    },
  };

  const t = content[language];

  return (
    <div className="bg-black py-8 md:py-12 px-4">
      <div className="container mx-auto max-w-4xl">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-zinc-800 rounded-full mb-4">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white">{t.title}</h1>
        </div>

        {/* Intro */}
        <div className="bg-zinc-900 rounded-xl p-6 mb-6">
          <p className="text-zinc-300 text-lg mb-6">{t.intro}</p>
          
          {/* Warranty Periods */}
          <div className="space-y-4">
            {t.periods.map((period, index) => (
              <div key={index} className="flex items-start gap-4 bg-zinc-800 rounded-lg p-4">
                <div className="flex-shrink-0">
                  <Clock className="h-6 w-6 text-green-500" />
                </div>
                <div>
                  <span className="text-white font-semibold">{period.item}:</span>
                  <span className="text-green-400 font-bold ml-2">{period.time}</span>
                  {period.note && (
                    <span className="text-zinc-400 text-sm block mt-1">{period.note}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Validity */}
        <div className="bg-zinc-900 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <CheckCircle className="h-6 w-6 text-green-500" />
            <h2 className="text-xl font-bold text-white">{t.validityTitle}</h2>
          </div>
          <p className="text-zinc-300">{t.validityText}</p>
        </div>

        {/* Excluded Cases */}
        <div className="bg-zinc-900 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <XCircle className="h-6 w-6 text-red-500" />
            <h2 className="text-xl font-bold text-white">{t.excludedTitle}</h2>
          </div>
          <ul className="space-y-3">
            {t.excluded.map((item, index) => (
              <li key={index} className="flex items-start gap-3 text-zinc-300">
                <span className="text-red-400 mt-1">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default WarrantyPage;
